import { type LinearSubstep, SimpleCsticId } from "~/Configurators/Linear/models";
import { type FC } from "react";
import { useSelector } from "react-redux";
import { SubstepType } from "~/Configurators/Linear/dtos";
import SubstepFrame from "~/Configurators/Linear/components/SubstepFrame";
import { ConfiguratorType, type ShaftConfiguratorType } from "~/Configurators/ConfiguratorType";
import { useConfiguratorType } from "~/Configurators/common/useConfiguratorType";
import { selectCstic } from "~/Configurators/Linear/redux/selectCstic";
import { TileContainer } from "~/Configurators/common/components/GenericTileSubstep/GenericTileSubstep";
import { useXmlConfiguratorTranslations } from "~/Configurators/common/useXmlConfiguratorTranslations";

import * as MetricCstics from "../metric/metricCsticConstants";
import * as InchCstics from "../inch/inchCsticConstants";
import { INCH_UNIT, METRIC_UNIT } from "~/Configurators/Shaft/common/commonShaftConstants";
import { SimpleCsticNumberField } from "~/Configurators/Shaft/components/SimpleCsticNumberField";
import { SimpleCsticSelectField } from "~/Configurators/Shaft/components/SimpleCsticSelectField";
import { useMinMaxCsticLabel } from "~/Configurators/Shaft/components/useMinMaxCsticLabel";
import { useTranslation } from "react-i18next";
import { KmatType } from "~/Configurators/Linear/kmatType";

interface Props {
  // Actually substepIndex
  csticIndex: number;
  // Actually substepAmount
  csticAmount: number;
  def: LinearSubstep;
}

export const CONFIGURATION = {
  [ConfiguratorType.MetricShaft]: {
    kmatType: KmatType.Main,
    metric: {
      value: MetricCstics.SHAFT_LENGTH,
      csticsToNull: [],
      min: MetricCstics.SHAFT_MIN_LENGTH,
      max: MetricCstics.SHAFT_MAX_LENGTH,
    },
    partAmount: MetricCstics.SHAFT_NUMBER_OF_PARTS,
  },
  [ConfiguratorType.InchShaft]: {
    kmatType: KmatType.Main,
    metric: {
      value: InchCstics.SHAFT_LENGTH_METRIC,
      csticsToNull: [InchCstics.SHAFT_LENGTH_INCH],
      min: InchCstics.SHAFT_LENGTH_METRIC_MIN,
      max: InchCstics.SHAFT_LENGTH_METRIC_MAX,
    },
    inch: {
      value: InchCstics.SHAFT_LENGTH_INCH,
      csticsToNull: [InchCstics.SHAFT_LENGTH_METRIC],
      min: InchCstics.SHAFT_LENGTH_INCH_MIN,
      max: InchCstics.SHAFT_LENGTH_INCH_MAX,
      decimalScale: 4,
    },
    partAmount: InchCstics.SHAFT_NUMBER_OF_PARTS,
  },
};

export const ShaftLengthSubstep: FC<Props> = ({ def, ...props }) => {
  const configuratorType = useConfiguratorType() as ShaftConfiguratorType;
  const config = CONFIGURATION[configuratorType];
  const xmlT = useXmlConfiguratorTranslations().t!;
  const { t } = useTranslation();

  const partAmountCsticId = {
    type: config.kmatType,
    index: 0,
    key: config.partAmount,
  };
  const partAmountCstic = useSelector(selectCstic(partAmountCsticId));

  const lengthCstic = useSelector(selectCstic({ type: config.kmatType, index: 0, key: config.metric.value }));
  const isSelected = lengthCstic?.value && +lengthCstic.value !== 0;

  return (
    <SubstepFrame
      title={def.name}
      description=""
      info={def.info}
      isPicked={!!isSelected}
      errorMessage={def.isInvalid ? t("linear.step.select.valid.value") : ""}
      showError={def.isInvalid}
      {...props}
    >
      <TileContainer columnAmount={4}>
        <div className="scha__linear-configurator__semi-steps__frame__input-block__millimeter-input">
          <InputField config={config.metric} kmatType={config.kmatType} unit={METRIC_UNIT} />
        </div>
        {"inch" in config && (
          <div className="scha__linear-configurator__semi-steps__frame__input-block__millimeter-input">
            <InputField config={config.inch} kmatType={config.kmatType} unit={INCH_UNIT} />
          </div>
        )}
        {partAmountCstic && (
          <div className="scha__linear-configurator__semi-steps__frame__input-block__millimeter-input">
            <SimpleCsticSelectField
              label={xmlT("configurator.shaft.step.2.substep.1.tile.1.title")}
              csticId={partAmountCsticId}
              substepType={SubstepType.ShaftLength}
              disabled={true}
              renderValue={(_, index) => xmlT(`configurator.shaft.step.2.substep.1.option.${index + 1}`)}
            />
          </div>
        )}
      </TileContainer>
    </SubstepFrame>
  );
};

interface InputFieldProps {
  config: {
    value: string;
    csticsToNull: string[];
    min: string;
    max: string;
    decimalScale?: number;
  };
  kmatType: KmatType;
  unit: string;
}

const InputField = ({ config: { value, csticsToNull, min, max, decimalScale }, kmatType, unit }: InputFieldProps) => {
  const helperLabel = useMinMaxCsticLabel({
    minCsticId: SimpleCsticId(min),
    maxCsticId: SimpleCsticId(max),
    unit,
  });

  const csticId = { key: value, type: kmatType, index: 0 };

  return (
    <SimpleCsticNumberField
      csticId={csticId}
      csticsToNull={csticsToNull}
      helperLabel={helperLabel}
      substepType={SubstepType.ShaftLength}
      useCsticLabel={true}
      decimalScale={decimalScale}
    />
  );
};
